import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { assetUrl, formatApiError } from "@/lib/api";
import { formatPrice, formatKm } from "@/lib/format";
import { TID } from "@/constants/tids";

const STAT = {
  nou: { label: "Disponibilă", cls: "bg-[#D4AF37]/15 text-[#B5952F]" },
  rezervata: { label: "Rezervată", cls: "bg-gray-100 text-gray-700" },
  vanduta: { label: "Vândută", cls: "bg-[#0A0A0A] text-white" },
};

export default function AdminCars() {
  const [cars, setCars] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/vehicles", { params: { limit: 200 } })
      .then((r) => setCars(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id, label) => {
    if (!window.confirm(`Ștergi mașina "${label}"? Această acțiune este ireversibilă.`)) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Mașină ștearsă");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = cars.filter((c) =>
    q ? `${c.brand} ${c.model}`.toLowerCase().includes(q.toLowerCase()) : true,
  );

  return (
    <div>
      <div className="flex flex-col gap-4 mb-6 md:mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
            Inventar
          </div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#0A0A0A]">Mașini</h1>
          <p className="mt-1 text-sm text-gray-500">
            {loading ? "Se încarcă..." : `${cars.length} mașini în stoc`}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              placeholder="Caută marcă / model"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 pr-3 h-12 rounded-xl bg-white border border-gray-200 text-sm outline-none focus:border-[#D4AF37] w-full sm:w-72"
              inputMode="search"
              autoComplete="off"
            />
          </div>
          <Link
            to="/admin/masini/nou"
            data-testid={TID.adminAddCar}
            className="inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#222] active:bg-black text-white px-5 h-12 rounded-full text-sm font-semibold transition-colors shrink-0"
          >
            <Plus className="h-4 w-4" />
            Adaugă mașină
          </Link>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FA]">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-gray-500">Mașină</th>
              <th className="px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-gray-500">An</th>
              <th className="px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-gray-500">Km</th>
              <th className="px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-gray-500">Preț</th>
              <th className="px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-gray-500">Status</th>
              <th className="px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-gray-500 text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((c) => (
              <tr key={c.id} className="hover:bg-[#F8F9FA]">
                <td className="px-5 py-4">
                  <div className="font-bold text-[#0A0A0A]">{c.brand} {c.model}</div>
                  <div className="text-xs text-gray-500">{c.body_type} · {c.fuel} · {c.transmission}</div>
                </td>
                <td className="px-5 py-4 text-gray-700">{c.year}</td>
                <td className="px-5 py-4 text-gray-700">{formatKm(c.mileage)}</td>
                <td className="px-5 py-4 font-bold text-[#0A0A0A]">{formatPrice(c.price)}</td>
                <td className="px-5 py-4">
                  <span className={`text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full ${STAT[c.status]?.cls || ""}`}>
                    {STAT[c.status]?.label || c.status}
                  </span>
                  {c.featured && (
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-[#0A0A0A] text-[#D4AF37]">
                      Recomandată
                    </span>
                  )}
                </td>
                <td className="px-5 py-4 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Link
                      to={`/admin/masini/${c.id}/editeaza`}
                      className="h-10 w-10 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600"
                      title="Editează"
                    >
                      <Pencil className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => remove(c.id, `${c.brand} ${c.model}`)}
                      className="h-10 w-10 rounded-lg hover:bg-red-50 flex items-center justify-center text-red-600"
                      title="Șterge"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="text-center py-16 text-gray-400 text-sm">
                  Nicio mașină în stoc.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {filtered.length === 0 && !loading && (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-sm text-gray-400">
            Nicio mașină în stoc.
          </div>
        )}
        {filtered.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="flex gap-3 p-3">
              <div className="h-24 w-32 shrink-0 rounded-xl overflow-hidden bg-gray-100">
                <img
                  src={c.images?.[0] ? assetUrl(c.images[0]) : "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400"}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.14em] text-gray-400 font-bold">
                  {c.brand}
                </div>
                <div className="font-bold text-[#0A0A0A] truncate">{c.model}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {c.year} · {formatKm(c.mileage)}
                </div>
                <div className="text-base font-extrabold text-[#0A0A0A] mt-1">
                  {formatPrice(c.price)}
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  <span className={`text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${STAT[c.status]?.cls || ""}`}>
                    {STAT[c.status]?.label || c.status}
                  </span>
                  {c.featured && (
                    <span className="text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-[#0A0A0A] text-[#D4AF37]">
                      Recomandată
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 border-t border-gray-100">
              <Link
                to={`/admin/masini/${c.id}/editeaza`}
                className="flex items-center justify-center gap-2 py-3 text-sm font-semibold text-[#0A0A0A] active:bg-gray-100 border-r border-gray-100"
              >
                <Pencil className="h-4 w-4" />
                Editează
              </Link>
              <button
                onClick={() => remove(c.id, `${c.brand} ${c.model}`)}
                className="flex items-center justify-center gap-2 py-3 text-sm font-semibold text-red-600 active:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Șterge
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
